import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES } from "@src/appV2/api";
import { compareAsc, isAfter, parseISO } from "date-fns";

import {
  InterviewStatus,
  type InterviewWithDetails,
} from "../../WorkerShifts/api/useFetchPaginatedInterviews";
import {
  type GetPlacementApplicationsResponse,
  type PlacementApplicationData,
  type PlacementApplicationInterviewInclude,
} from "../types/fetchPlacementApplications.schema";

// Only Booked and AttendanceConfirmed interviews are actionable for the worker.
// Cancelled, Rescheduled, NoCallNoShow, and Pending interviews belong in the
// Sent Applications section, not the Interviews section.
const ACTIONABLE_INTERVIEW_STATUSES = new Set<InterviewStatus>([
  InterviewStatus.BOOKED,
  InterviewStatus.ATTENDANCE_CONFIRMED,
]);

/**
 * Extracts interview details from a placement-applications response,
 * keeping only actionable interviews (Booked, AttendanceConfirmed) and
 * sorting by start date ascending (soonest first).
 *
 * This replaces the separate /interviews endpoint fetch for the My Jobs tab.
 */
export function extractInterviewsFromPlacementApplications(
  applications: PlacementApplicationData[],
  included: GetPlacementApplicationsResponse["included"]
): InterviewWithDetails[] {
  const interviews: InterviewWithDetails[] = [];

  for (const application of applications) {
    const placementInclude = findInclude(
      included,
      API_TYPES.placement,
      application.relationships.placement.data.id
    );

    const workplaceInclude = findInclude(
      included,
      API_TYPES.workplace,
      application.relationships.workplace.data.id
    );

    const interviewIncludes = application.relationships.interviews.data
      .map((ref) => findInclude(included, API_TYPES.interview, ref.id))
      .filter(
        (item): item is PlacementApplicationInterviewInclude =>
          isDefined(item) && item.type === API_TYPES.interview
      );

    for (const interview of interviewIncludes) {
      if (!ACTIONABLE_INTERVIEW_STATUSES.has(interview.attributes.status)) {
        continue;
      }

      const banId = interview.relationships?.placementCandidateBan?.data.id;
      const banResult = isDefined(banId)
        ? findInclude(included, API_TYPES.placementCandidateBan, banId)
        : undefined;
      const isBanExcused =
        isDefined(banResult) && banResult.type === API_TYPES.placementCandidateBan
          ? banResult.meta.isExcused
          : false;

      interviews.push({
        id: interview.id,
        type: API_TYPES.interview,
        start: parseISO(interview.attributes.start),
        end: parseISO(interview.attributes.end),
        status: interview.attributes.status,
        scheduleStatus: interview.attributes.scheduleStatus,
        isBanExcused,
        placementId: application.relationships.placement.data.id,
        placementApplicationId: application.id,
        placementCandidateId: "",
        ...(isDefined(placementInclude) &&
          placementInclude.type === API_TYPES.placement && {
            placement: {
              id: placementInclude.id,
              workerTypes: placementInclude.attributes.workerTypes,
            },
          }),
        ...(isDefined(workplaceInclude) &&
          workplaceInclude.type === API_TYPES.workplace &&
          isDefined(placementInclude) &&
          placementInclude.type === API_TYPES.placement && {
            workplace: {
              id: workplaceInclude.id,
              name: workplaceInclude.attributes.name,
              formattedAddress: undefined,
              distance: placementInclude.meta?.distance,
              phone: undefined,
            },
          }),
      });
    }
  }

  return interviews.toSorted((a, b) => compareAsc(a.start, b.start));
}

/**
 * Returns true if the application has at least one actionable (Booked or
 * AttendanceConfirmed) interview starting after `now`. Used to exclude
 * applications already shown in the Interviews section from the Sent
 * Applications accordion — applications with only cancelled/rescheduled
 * interviews should fall back into Sent Applications.
 */
export function applicationHasUpcomingInterview(
  application: PlacementApplicationData,
  included: GetPlacementApplicationsResponse["included"],
  now: Date
): boolean {
  const interviewIncludes = application.relationships.interviews.data
    .map((ref) => findInclude(included, API_TYPES.interview, ref.id))
    .filter(
      (item): item is PlacementApplicationInterviewInclude =>
        isDefined(item) && item.type === API_TYPES.interview
    );

  return interviewIncludes.some(
    (interview) =>
      ACTIONABLE_INTERVIEW_STATUSES.has(interview.attributes.status) &&
      isAfter(parseISO(interview.attributes.start), now)
  );
}

type IncludeItem = NonNullable<GetPlacementApplicationsResponse["included"]>[number];

function findInclude(
  included: GetPlacementApplicationsResponse["included"],
  type: string,
  id: string
): IncludeItem | undefined {
  return (included ?? []).find((item) => item.type === type && item.id === id);
}
